import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Brain, Gamepad2 } from 'lucide-react';

// Lazy load the game component for performance
const DinoGame = lazy(() => import('./DinoGame'));

interface AnalysisLoadingScreenProps {
  progress?: number;
  message?: string;
  title?: string;
  subtitle?: string;
}

const AnalysisLoadingScreen: React.FC<AnalysisLoadingScreenProps> = ({
  progress,
  message,
  title = "Analyzing Creative...",
  subtitle = "This usually takes 1-5 minutes depending on creative complexity"
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [showGame, setShowGame] = useState(false);

  // Reset game when component unmounts (analysis complete)
  useEffect(() => {
    return () => {
      setShowGame(false);
    };
  }, []);

  // Simulate realistic progress if no actual progress is provided
  useEffect(() => {
    if (progress !== undefined && progress > 0) {
      setDisplayProgress(progress);
      return;
    }

    // Simulate realistic analysis progress
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const seconds = elapsed / 1000;
      
      // Slower, more realistic progress curve
      let newProgress = 0;
      if (seconds < 10) {
        newProgress = Math.min(15, seconds * 1.5); // First 10s: 0-15%
      } else if (seconds < 30) {
        newProgress = Math.min(35, 15 + (seconds - 10) * 1); // Next 20s: 15-35%
      } else if (seconds < 60) {
        newProgress = Math.min(60, 35 + (seconds - 30) * 0.8); // Next 30s: 35-60%
      } else if (seconds < 120) {
        newProgress = Math.min(85, 60 + (seconds - 60) * 0.4); // Next 60s: 60-85%
      } else {
        newProgress = Math.min(95, 85 + (seconds - 120) * 0.1); // After 2min: 85-95%
      }
      
      setDisplayProgress(Math.round(newProgress));
    }, 1000);

    return () => clearInterval(interval);
  }, [progress]);

  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center py-8">
      {/* Animated loader */}
      <div className="relative mb-8">
        <div className="w-32 h-32 rounded-full bg-purple-100 flex items-center justify-center">
          <Brain className="w-16 h-16 text-purple-600 animate-pulse" />
        </div>
        <div className="absolute inset-0 w-32 h-32 rounded-full border-4 border-purple-600 border-t-transparent animate-spin" />
      </div>

      {/* Progress */}
      <div className="w-full max-w-md mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Analysis Progress</span>
          <span className="text-sm text-gray-500">{displayProgress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
      </div>

      {/* Status message */}
      <motion.div
        key={message}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-lg"
      >
        <p className="text-lg text-gray-700 mb-2">{title}</p>
        <p className="text-sm text-gray-500 mb-4">
          {message || subtitle}
        </p>
        
        {/* Game toggle button */}
        {!showGame && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            onClick={() => setShowGame(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all shadow-sm"
          >
            <Gamepad2 className="w-4 h-4" />
            <span>Play Dino Run while waiting</span>
          </motion.button>
        )}
      </motion.div>

      {/* Game component */}
      {showGame && (
        <Suspense fallback={
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-2 text-sm text-gray-500">Loading game...</span>
          </div>
        }>
          <DinoGame 
            onClose={() => setShowGame(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default AnalysisLoadingScreen;